import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Calendar } from "antd";

export type RangeCalendarValue = { start: any; end: any | null };

type Props = {
  maxRangeDays?: number;
  allowRangeSelection?: boolean;
  value?: RangeCalendarValue | null;
  onChange?: (range: RangeCalendarValue) => void;
  onRangeDone?: (range: RangeCalendarValue) => void;
};

const RangeCalendarContainer = styled.div`
  width: 320px;

  .ant-picker-calendar {
    width: 320px;
  }
`;

export default function RangeCalendar({
  maxRangeDays = 30,
  allowRangeSelection = true,
  value,
  onChange,
  onRangeDone,
}: Props) {
  const [start, setStart] = useState<any | null>(null);
  const [end, setEnd] = useState<any | null>(null);
  const [option, setOption] = useState('month');

  const isClickingDateRef = useRef(false);

  useEffect(() => {
    setStart(value?.start ? value.start.clone() : null);
    setEnd(value?.end ? value.end.clone() : null);
  }, [value]);

  const handleMouseDownCapture = (e: any) => {
    const target = e.target as HTMLElement;

    const isDateCell = target.closest(".ant-picker-cell");

    isClickingDateRef.current = !!isDateCell;
  };

  const handleSelect = useCallback(
    (date: any) => {
      if (!isClickingDateRef.current) {
        return;
      }

      const selected = option === 'year' ? date.clone().startOf("month") : date.clone().startOf("day");
      
      if (!allowRangeSelection) {
        setStart(selected);
        setEnd(selected);
        onChange?.({ start: selected, end: selected });
        onRangeDone?.({ start: selected, end: selected });
        return;
      }

      if (!start || (start && end)) {
        setStart(selected);
        setEnd(null);
        onChange?.({ start: selected, end: null });
        return;
      }

      const prevStart = start;
      const diff = selected.diff(prevStart, "day");
      const rangeLength = Math.abs(diff);

      if (rangeLength > maxRangeDays) return;

      if (diff < 0) {
        setStart(selected);
        setEnd(prevStart);
        onChange?.({ start: selected, end: prevStart });
        onRangeDone?.({ start: selected, end: prevStart });
        return;
      }

      setEnd(selected);
      onChange?.({ start: prevStart, end: selected });
      onRangeDone?.({ start: prevStart, end: selected });
    },
    [allowRangeSelection, end, maxRangeDays, onChange, onRangeDone, start, option],
  );

  const dateFullCellRender = useCallback(
    (value: any) => {
      const isStart = start ? value.isSame(start, "day") : false;
      const isEnd = end ? value.isSame(end, "day") : false;
      const isInRange =
        start &&
        end &&
        value.isAfter(start, "day") &&
        value.isBefore(end, "day");

      const style: React.CSSProperties = {
        background: isStart || isEnd ? "#1890ff" : isInRange ? "#e6f4ff" : "",
        color: isStart || isEnd ? "#fff" : "",
        borderRadius: 4,
        padding: 2,
      };

      return <div style={style}>{value.date()}</div>;
    },
    [end, start],
  );

  return (
    <RangeCalendarContainer
      onClick={(e) => e.stopPropagation()}
      onMouseDownCapture={handleMouseDownCapture}
    >
      <Calendar
        fullscreen={false}
        onSelect={handleSelect}
        dateFullCellRender={dateFullCellRender}
        onPanelChange={(_, mode) => {
            setOption(mode);
        }}
      />
    </RangeCalendarContainer>
  );
}